/**
 * Thmanyah — the face a designed post is set in, and the way it is really loaded.
 *
 * Everything here was read off the shipped files rather than assumed, and
 * `__tests__/fonts.test.ts` re-reads them so it stays true:
 *
 *  1. **No two of these faces are the same family.** `serifdisplay-Regular` and
 *     `serifdisplay-Bold` are one family with a Regular and a Bold subfamily, but
 *     `serifdisplay-Black` is a family of its own (`thmanyah serif display Black`,
 *     subfamily *Regular*), and Sans is a third. Asking for a family and then for a
 *     weight therefore cannot reach these files — a design has to name the *face*.
 *  2. **Each face carries one PostScript name**, matching its own file name
 *     (`thmanyahserifdisplay-Bold`); that is what makes the file on disk and the
 *     name in code checkable against each other (`THMANYAH_FACE_FILES`).
 *  3. **The faces are loaded at runtime under our own keys, on every platform**
 *     (`useThmanyahFonts`). That is what fixes the name: iOS resolves a family from
 *     what is inside the file and Android from the asset it was handed, so neither
 *     is asked to agree — the key a face is loaded with *is* the name a design uses.
 *     (`app.config.js` still embeds the same files for a cold start; registering the
 *     same face twice is harmless.)
 *  4. **Each platform is handed the format it reads**: the shipped `.otf` on iOS and
 *     Android, the shipped `.woff2` in a browser. The web requires live inside
 *     `thmanyahWebSources`, which nothing on a device ever calls, so a native
 *     bundle never pays for — or trips over — the web format.
 *
 * Nothing else is loaded at app start: a reader who never opens a designed post
 * should not pay for its typography, so the faces are fetched by the design itself,
 * and a design draws nothing until they are in (`useThmanyahFonts`) rather than
 * flashing the machine's own Arabic face for a frame.
 *
 * Four faces ship — the three weights of Serif Display a design's words are drawn
 * in, and Sans Medium for the controls around them. Every other weight of every
 * family would be megabytes carried by everyone who never designs a post.
 */
import {Platform, type TextStyle} from 'react-native'
import {useFonts} from 'expo-font'

import {getThmanyahSources, thmanyahNativeSources} from '#/state/postStyle/getThmanyahSources'
import {type StyledPostWeight} from '#/state/postStyle/types'

/**
 * Serif Display, by weight — the family a design's own words are set in.
 *
 * The names are ours, deliberately: they are the keys the files are loaded under,
 * so they are exactly what the platform has registered (see the note above).
 */
export const THMANYAH_DISPLAY = {
  regular: 'ThmanyahSerifDisplay-Regular',
  bold: 'ThmanyahSerifDisplay-Bold',
  black: 'ThmanyahSerifDisplay-Black',
} as const

/** Sans, by weight — one face, for the controls around a design. */
export const THMANYAH_SANS = {
  medium: 'ThmanyahSans-Medium',
} as const

/** Every face the app ships, for the tests that hold them to their files. */
export const THMANYAH_FACES: readonly string[] = [
  THMANYAH_DISPLAY.regular,
  THMANYAH_DISPLAY.bold,
  THMANYAH_DISPLAY.black,
  THMANYAH_SANS.medium,
]

/** The weights a family actually has, in the order a picker offers them. */
export const THMANYAH_FAMILY_WEIGHTS = {
  display: ['regular', 'bold', 'black'],
  sans: ['medium'],
} as const satisfies {
  display: readonly StyledPostWeight[]
  sans: readonly string[]
}

/** The weight a design is set in when the author did not choose one. */
export const THMANYAH_DEFAULT_WEIGHT: StyledPostWeight = 'bold'

/**
 * The shipped file behind each loaded name.
 *
 * The two sides differ only in case, and keeping both means a re-subset that
 * renames a file is caught by a test rather than by a card drawn in the fallback.
 */
export const THMANYAH_FACE_FILES = {
  [THMANYAH_DISPLAY.regular]: 'thmanyahserifdisplay-Regular',
  [THMANYAH_DISPLAY.bold]: 'thmanyahserifdisplay-Bold',
  [THMANYAH_DISPLAY.black]: 'thmanyahserifdisplay-Black',
  [THMANYAH_SANS.medium]: 'thmanyahsans-Medium',
} as const

/**
 * How much ink each face draws, as a multiple of the type size.
 *
 * Measured off the files (`yMax - yMin` over the em in `head`). Thmanyah is an
 * Arabic serif whose outlines reach about 1.8 em from the lowest descender to the
 * highest dot — half again the 1.2 em of an ordinary Latin face — so a line height
 * copied from the app's own type would put one line's dots inside the line above
 * it. The type suite re-measures the files, so a re-cut face is caught instead of
 * colliding.
 */
export const THMANYAH_INK_RATIO = {
  regular: 1.797,
  bold: 1.874,
  black: 1.911,
  sansMedium: 1.819,
} as const

/**
 * The leading a design's lines are given, as a multiple of the type size.
 *
 * Between the app's own 1.3 and the ink the face can reach: a full 1.8 em of
 * leading would make a three-line card a poster, and the ink that needs it — deep
 * descenders under high dots — almost never meets itself on two adjacent lines of
 * ordinary prose. The suite holds this number against the measured ink so the guess
 * cannot drift away from the face it was made for.
 */
export const THMANYAH_LINE_HEIGHT = 1.7

/** The face a design's words are drawn in, for a weight. */
export function thmanyahFace(weight?: StyledPostWeight): string {
  return (
    THMANYAH_DISPLAY[
      weight && weight in THMANYAH_DISPLAY
        ? (weight as keyof typeof THMANYAH_DISPLAY)
        : THMANYAH_DEFAULT_WEIGHT
    ] ?? THMANYAH_DISPLAY[THMANYAH_DEFAULT_WEIGHT]
  )
}

/** The ink a face draws, as a multiple of the type size. */
export function thmanyahInkRatio(weight?: StyledPostWeight): number {
  const key = (weight ??
    THMANYAH_DEFAULT_WEIGHT) as keyof typeof THMANYAH_INK_RATIO
  return THMANYAH_INK_RATIO[key] ?? THMANYAH_INK_RATIO.bold
}

/**
 * A `fontFamily` value for a design's text.
 *
 * On the web a family list is a per-glyph contract: the browser reaches for
 * Thmanyah for the Arabic it was drawn for, and slides to the app's own Inter — then
 * to the generic serif — for anything the face has no glyph for, rather than
 * dropping the whole line to the machine's default. On iOS and Android a
 * comma-separated list is not a fallback at all: it is one family name that fails to
 * resolve, and the text silently leaves the face. So a device is handed exactly one
 * family, and the web is handed the stack.
 *
 * The platform is a parameter as well as a default so every branch is reachable from
 * a test rather than only from the machine the test happens to run on.
 */
export function thmanyahFontFamily(
  weight?: StyledPostWeight,
  platform: string = Platform.OS,
): string {
  const face = thmanyahFace(weight)
  if (platform !== 'web') return face
  return [face, 'InterVariable', 'serif'].join(', ')
}

/**
 * The Mursala elongation, as the platform in front of us spells it.
 *
 * Mursala is the face's own OpenType stylistic set `ss01` — present in all four
 * shipped files, which the suite checks — but no two platforms reach a feature the
 * same way: iOS takes `fontVariant: ['stylistic-one']` and *ignores*
 * `fontFeatureSettings`, Android and the browser take the raw tag in
 * `fontFeatureSettings` and RN's `fontVariant` is the one Android drops. A design
 * draws Mursala through this and nothing else, so a platform cannot quietly lose the
 * effect.
 *
 * Both are style *requests*, never substitutions: a face without the set draws
 * ordinary, correctly shaped Arabic rather than something else pretending to be
 * elongated.
 */
export function mursalaStyle(platform: string = Platform.OS): TextStyle {
  if (platform === 'ios') return {fontVariant: ['stylistic-one']}
  /* Android and the web read the tag; React Native's own types for this version
     only declare `fontVariant`, so the tag is asserted rather than renamed. */
  return {fontFeatureSettings: '"ss01" 1'} as unknown as TextStyle
}

export {thmanyahNativeSources}

/**
 * Load the faces a design needs, and report whether they are in.
 *
 * Reported rather than fired and forgotten: a design *holds its text back* until
 * the answer is true (`components/Post/PostDesignRenderer`), because the correct
 * type is the whole of the design's promise, and a design drawn a moment early is a
 * design drawn in the wrong face.
 *
 * The source map is built once per platform and never on the other format's behalf,
 * so a native run never touches the web assets and vice versa.
 */
let sources: Record<string, number> | undefined

/** The faces to load for the platform in front of us, built once. */
export function thmanyahSources(): Record<string, number> {
  if (!sources) {
    sources = getThmanyahSources()
  }
  return sources
}

export function useThmanyahFonts(): boolean {
  const [loaded] = useFonts(thmanyahSources())
  return loaded
}
